import { Chip, Skeleton, Tooltip } from '@mui/material'
import { FunctionComponent } from 'react'

import { useIsiPrint } from '../hooks/useIsiPrint.ts'
import { ISI_PRINTER_STATUS_LABEL } from '../services/isiPrintClient.ts'

interface Props {
  /** Valor de `impresora.atributo1`: ID de hardware de isi-print, nombre del sistema o IP:puerto (red) */
  impresoraSistema?: string | null
}

/**
 * Estado de una impresora registrada según isi-print en este equipo: lista, apagada, sin papel...
 * Comparte la verificación en caché de `useIsiPrint`, no genera peticiones por fila.
 */
const ImpresoraEstadoChip: FunctionComponent<Props> = ({ impresoraSistema }) => {
  const isi = useIsiPrint()

  if (isi.estado === 'VERIFICANDO') return <Skeleton variant="rounded" width={70} height={24} />

  if (!impresoraSistema) {
    return <Chip size="small" variant="outlined" label="Sin vincular" sx={{ fontStyle: 'italic' }} />
  }

  if (!isi.listo) {
    return (
      <Tooltip title="isi-print no está listo en este equipo">
        <Chip size="small" variant="outlined" label="Sin verificar" />
      </Tooltip>
    )
  }

  const { impresora, coincidencia, status } = isi.estadoImpresora(impresoraSistema)

  // Impresora de red que isi-print de esta caja aún no tiene (no sincronizada o isi-print anterior a v1.3.2)
  if (status === 'red') {
    return (
      <Tooltip
        title={
          isi.errorRed ??
          'isi-print de esta caja aún no recibió esta impresora de red. Pulse Escanear para sincronizar.'
        }
      >
        <Chip size="small" variant="outlined" color="warning" label="Sin sincronizar" />
      </Tooltip>
    )
  }

  // Impresora de red de la sucursal entregada a isi-print: su estado (online / offline) lo da el agente
  if (impresora?.type === 'network') {
    return (
      <Tooltip
        title={`Red de la sucursal · ${impresora.label || impresora.name} · ${impresora.id}${
          impresora.managed ? '' : ' · registrada a mano en el panel de isi-print'
        }`}
      >
        <Chip
          size="small"
          variant="outlined"
          color={impresora.status === 'online' ? 'success' : 'error'}
          label={ISI_PRINTER_STATUS_LABEL[impresora.status] ?? 'No responde'}
        />
      </Tooltip>
    )
  }
  if (status === 'no-encontrada' || status === 'desconocido' || !impresora) {
    return (
      <Tooltip title="isi-print no encontró esta impresora en este equipo">
        <Chip size="small" variant="outlined" label="No detectada" />
      </Tooltip>
    )
  }

  const lista = status === 'online'
  const tooltip = [
    `${impresora.name} · ${impresora.port}`,
    coincidencia !== 'id' ? 'Guardada por nombre: edítela para guardar su ID de hardware' : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <Tooltip title={tooltip}>
      <Chip
        size="small"
        variant="outlined"
        color={lista ? (coincidencia === 'id' ? 'success' : 'warning') : 'error'}
        label={ISI_PRINTER_STATUS_LABEL[status]}
      />
    </Tooltip>
  )
}

export default ImpresoraEstadoChip
